import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Crown, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Win-Back magic-link redemption landing page.
 *
 * Route: /r/:token (public — no auth required).
 *
 * Flow:
 *   1. POST /api/vip/redeem-winback with the token.
 *   2. On success → show "VIP attivato" with two CTAs:
 *      - "Apri l'app RVINCENTE" (custom scheme `rvincente://` for installed app)
 *      - "Continua sul web"
 *   3. On error (token expired / already consumed / link malformed) →
 *      show friendly message + CTA to open the app anyway.
 *
 * The token is the user's identity proof — no login required. The VIP
 * activation is server-side, so it propagates to BOTH the native app and
 * the web app the next time the user opens either one.
 */
const WinbackRedeemPage = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const redeem = async () => {
      if (!token) {
        setError('Link non valido o incompleto.');
        setStatus('error');
        return;
      }
      try {
        const r = await axios.post(`${API}/vip/redeem-winback`, { token });
        if (cancelled) return;
        setData(r.data);
        setStatus('success');
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || 'Errore durante il riscatto. Riprova più tardi.';
        setError(detail);
        setStatus('error');
      }
    };
    redeem();
    return () => { cancelled = true; };
  }, [token]);

  const formatExpiry = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return ''; }
  };

  const openNativeApp = () => {
    // Try custom scheme first (installed app intercepts it)
    window.location.href = 'rvincente://home';
    // Fallback after 1.2s if scheme didn't fire → go to web home
    setTimeout(() => {
      if (!document.hidden) window.location.href = '/dashboard';
    }, 1200);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md" data-testid="winback-redeem-page">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 mb-3">
            <Crown className="w-9 h-9 text-black" />
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight">RVINCENTE</h1>
          <p className="text-[12px] text-gray-500 mt-1 uppercase tracking-widest">Bentornato</p>
        </div>

        {status === 'loading' && (
          <div className="bg-[#1A1A1A] border border-[#2D2D2D] rounded-2xl p-8 text-center" data-testid="winback-loading">
            <Loader2 className="w-10 h-10 text-amber-400 animate-spin mx-auto" />
            <p className="text-gray-400 text-sm mt-4">Stiamo attivando il tuo VIP…</p>
          </div>
        )}

        {status === 'success' && data && (
          <div className="bg-gradient-to-br from-amber-500/10 via-[#1A1A1A] to-[#1A1A1A] border border-amber-500/40 rounded-2xl p-6 text-center" data-testid="winback-success">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/20 mb-4">
              <CheckCircle2 className="w-9 h-9 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-amber-300 mb-2">
              {data.already_active ? 'VIP già attivo!' : 'VIP attivato!'}
            </h2>
            <p className="text-gray-300 text-sm leading-relaxed mb-1">
              Hai <b className="text-amber-300">{data.days} {data.days === 1 ? 'giorno' : 'giorni'} VIP</b> per tornare a vincere con noi.
            </p>
            {data.expires_at && (
              <p className="text-gray-500 text-[12px] mb-6">
                Valido fino al <b className="text-gray-300">{formatExpiry(data.expires_at)}</b>
              </p>
            )}
            <div className="space-y-2">
              <button
                onClick={openNativeApp}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:opacity-95 active:scale-[0.98] transition-transform"
                data-testid="winback-open-app-btn"
              >
                <ExternalLink className="w-4 h-4" />
                Apri l'app RVINCENTE
              </button>
              <Link
                to="/dashboard"
                className="block text-center text-amber-300/80 text-sm py-2 hover:text-amber-300"
                data-testid="winback-continue-web-link"
              >
                Continua sul web →
              </Link>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-[#1A1A1A] border border-red-500/40 rounded-2xl p-6 text-center" data-testid="winback-error">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-500/20 mb-4">
              <AlertTriangle className="w-9 h-9 text-red-400" />
            </div>
            <h2 className="text-lg font-bold text-red-300 mb-2">Ops!</h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {error}
            </p>
            <button
              onClick={openNativeApp}
              className="w-full bg-[#242424] text-white font-semibold py-3 rounded-xl border border-[#333] hover:border-amber-500/50"
              data-testid="winback-error-open-app-btn"
            >
              Apri RVINCENTE comunque
            </button>
            <Link
              to="/dashboard"
              className="block mt-3 text-gray-500 text-sm py-2 hover:text-gray-300"
              data-testid="winback-error-go-home-link"
            >
              Vai alla home web
            </Link>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-gray-600 text-[11px] mt-8">
          Hai ricevuto questa email perché sei iscritto a RVINCENTE.
        </p>
      </div>
    </div>
  );
};

export default WinbackRedeemPage;
